import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@tinct/web-components';
import { updateLeadFormSchema, type UpdateLeadForm } from '@tinct/core-api-models';
import { useGetLeadForm, useUpdateLeadForm } from '@tinct/core-api';
import { useCurrentWorkspaceUrlPrefix } from '@tinct/core-workspace';
import { LeadFormEditor, readLeadFormToUpdate } from '../../../../components/lead-form';

export type LeadFormSectionProps = {
  landingPageId: string;
};

/**
 * Page-scoped lead-form settings, backed by the singleton endpoints. On this
 * surface the page is already generated, so the field list is DERIVED from
 * the rendered form markup (re-derived on every version) and shown
 * read-only; `enabled`, `destination` and `successMessage` stay user-owned
 * and apply live.
 */
export function LeadFormSection({ landingPageId }: LeadFormSectionProps) {
  const workspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();

  const { data: leadForm, isFetched } = useGetLeadForm(
    { landingPageId },
    // Fields re-derive server-side on every generated/edited version — with
    // the app-wide staleTime: Infinity a cached copy would go stale forever.
    { refetchOnMount: 'always' },
  );

  // Draft of the user-owned bits, derived once from the fetched config;
  // render-derived to avoid a reset effect.
  const [draft, setDraft] = useState<UpdateLeadForm | null>(null);
  if (isFetched && draft === null) {
    setDraft(readLeadFormToUpdate(leadForm));
  }

  const [validationError, setValidationError] = useState<string | null>(null);

  const updateMutation = useUpdateLeadForm({
    onSuccess: () => toast.success('Lead form saved'),
  });

  // The derived field list always comes from the latest fetch, never the
  // draft — a refetch after a new version updates the display immediately.
  const derivedFields = readLeadFormToUpdate(leadForm).fields;
  const editorValue = draft ? { ...draft, fields: derivedFields } : null;

  const handleSave = () => {
    if (!editorValue) return;
    if (editorValue.enabled) {
      const parsed = updateLeadFormSchema.safeParse(editorValue);
      if (!parsed.success) {
        setValidationError(parsed.error.issues[0]?.message ?? 'Invalid form');
        return;
      }
    }
    setValidationError(null);
    // PUT with the derived fields echoed back — the backend re-derives them
    // from the page anyway; only the user-owned bits are really changing.
    updateMutation.mutate({ landingPageId, data: editorValue });
  };

  // Surface the server rejection (e.g. hubspot without a READY integration).
  const displayedError = validationError ?? updateMutation.error?.error ?? null;

  if (!editorValue) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="font-heading text-xl font-semibold tracking-tight">
          Lead form
        </h2>
        <p className="text-sm text-muted-foreground">
          Capture leads on this landing page. The destination and success
          message apply immediately; the fields mirror the form rendered on
          the page.
        </p>
      </div>

      <LeadFormEditor
        value={editorValue}
        onChange={(next) => setDraft(next)}
        integrationsUrl={`${workspaceUrlPrefix}/settings/integrations`}
        fieldsReadOnly
      />

      {displayedError && (
        <p className="text-sm text-destructive">{displayedError}</p>
      )}

      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground">
          Submissions from the studio preview are real — they land in your
          leads list.
        </p>
        <Button onClick={handleSave} isLoading={updateMutation.isPending}>
          Save
        </Button>
      </div>
    </div>
  );
}
